import { ChangeEvent, FocusEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EMAIL_MAX_LENGTH,
  getEmailErrorKey,
  isEmailEmpty,
  isValidEmail,
  normalizeEmailInput,
} from '../utils/email';

export interface UseEmailFieldOptions {
  /** Current value of the field. */
  value?: string | null;
  /** Called with the already normalized (lower cased, whitespace free) value. */
  onChange: (value: string) => void;
  /** When true an empty value is reported as an error. */
  required?: boolean;
  /** Set to true once the user tried to submit, so errors show without waiting for a blur. */
  submitting?: boolean;
  /** Read only fields never display validation errors. */
  disabled?: boolean;
}

export interface EmailFieldProps {
  type: 'email';
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  error: boolean;
  helperText: string | undefined;
  sx: { '& input': { textTransform: 'lowercase' } };
  slotProps: {
    htmlInput: {
      inputMode: 'email';
      autoCapitalize: 'none';
      autoCorrect: 'off';
      spellCheck: false;
      maxLength: number;
    };
  };
}

export interface EmailFieldApi {
  /** Props to spread on a MUI `TextField` / `TextValidator`. */
  fieldProps: EmailFieldProps;
  /** True when the current value passes the rules (empty is valid for optional fields). */
  isValid: boolean;
  isEmpty: boolean;
  /** True when an error is currently displayed. */
  hasError: boolean;
  /** Localized error message, or an empty string. */
  errorMessage: string;
  /** Marks the field as touched and returns whether the value is valid — use it before saving. */
  validate: () => boolean;
  /** Clears the touched flag (e.g. when cancelling an edit). */
  reset: () => void;
}

/**
 * Wires the shared email rules into a form field:
 *  - every keystroke is normalized, so upper case letters are replaced by lower case ones,
 *  - basic validation runs once the field has been blurred or the form submitted,
 *  - `validate()` lets the caller block a save on an invalid address.
 */
export function useEmailField({
  value,
  onChange,
  required = false,
  submitting = false,
  disabled = false,
}: UseEmailFieldOptions): EmailFieldApi {
  const { t } = useTranslation();
  const [isTouched, setIsTouched] = useState(false);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(normalizeEmailInput(event.target.value));
    },
    [onChange]
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsTouched(true);
      // Normalize once more on blur to catch programmatic/autofilled values.
      const normalized = normalizeEmailInput(event.target.value);
      if (normalized !== (value ?? '')) {
        onChange(normalized);
      }
    },
    [onChange, value]
  );

  const errorKey = getEmailErrorKey(value, required);
  const hasError = !disabled && Boolean(errorKey) && (isTouched || submitting);
  const errorMessage = hasError && errorKey ? t(errorKey) : '';

  const validate = useCallback(() => {
    setIsTouched(true);
    return required ? isValidEmail(value) : isEmailEmpty(value) || isValidEmail(value);
  }, [required, value]);

  const reset = useCallback(() => setIsTouched(false), []);

  const fieldProps = useMemo<EmailFieldProps>(
    () => ({
      type: 'email',
      value: value ?? '',
      onChange: handleChange,
      onBlur: handleBlur,
      error: hasError,
      helperText: errorMessage || undefined,
      sx: { '& input': { textTransform: 'lowercase' } },
      slotProps: {
        htmlInput: {
          inputMode: 'email',
          autoCapitalize: 'none',
          autoCorrect: 'off',
          spellCheck: false,
          maxLength: EMAIL_MAX_LENGTH,
        },
      },
    }),
    [value, handleChange, handleBlur, hasError, errorMessage]
  );

  return {
    fieldProps,
    isValid: required ? isValidEmail(value) : isEmailEmpty(value) || isValidEmail(value),
    isEmpty: isEmailEmpty(value),
    hasError,
    errorMessage,
    validate,
    reset,
  };
}

export default useEmailField;
